/**
 * When may a stashed homepage clone intent replay itself? (PANDA-1618)
 *
 * Two different gates can park a clone, and they must NOT resume the same way:
 *
 *   - The LOGIN gate. The user clicked "Clone this" while signed out. Signing
 *     in is itself the confirmation, so the clone fires the moment the session
 *     lands — that's the behaviour the homepage has always had.
 *
 *   - The EMAIL-VERIFICATION gate. The user was already signed in; a credit
 *     spend came back 403. The gate modal reloads the page once it sees the
 *     account verify, leaving `panda:just-verified` behind. That reload — and
 *     only that reload — is an unambiguous "the user is right here, finishing
 *     the thing they started", so it auto-resumes. Any OTHER load carrying a
 *     verification-parked intent (they verified on their phone, closed the tab,
 *     came back an hour later) must ask first: resuming spends a build credit,
 *     and an unattended charge is worse than an extra tap.
 *
 * Pure on purpose — the homepage owns the storage reads and the navigation,
 * this owns the rule.
 */
import type { CloneIntent } from "@/lib/home-clone-stash";

export type CloneResumeDecision =
  /** Nothing to replay on this load. The caller falls through to its other
   *  resume paths (a stashed prompt, `?prompt=` in the URL). A verification-
   *  parked intent that hasn't been confirmed lands here too, and stays in
   *  storage for the explicit-resume prompt to offer later. */
  | { kind: "none" }
  /** An intent exists but another resume already claimed this load. The caller
   *  must stop rather than fall through — otherwise one visit could start two
   *  builds and spend two credits. */
  | { kind: "already_resumed" }
  /** Replay it now. `consumeJustVerified` is true when the decision was made
   *  off the post-verification reload, so the caller clears that flag and no
   *  second path can act on the same verification. */
  | {
      kind: "resume";
      intent: CloneIntent;
      trigger: "auth" | "verification";
      consumeJustVerified: boolean;
    };

export function decideCloneResume(input: {
  intent: CloneIntent | null;
  /** Whether `panda:just-verified` is present — PEEKED, not consumed. */
  justVerified: boolean;
  /** Whether this page load already fired a clone/build resume. */
  alreadyResumed: boolean;
}): CloneResumeDecision {
  const { intent, justVerified, alreadyResumed } = input;

  if (!intent) return { kind: "none" };
  if (alreadyResumed) return { kind: "already_resumed" };

  if (intent.pendingVerification) {
    if (!justVerified) return { kind: "none" };
    return {
      kind: "resume",
      intent,
      trigger: "verification",
      consumeJustVerified: true,
    };
  }

  return {
    kind: "resume",
    intent,
    trigger: "auth",
    consumeJustVerified: false,
  };
}
